import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Mail, Copy, CheckCircle2, ExternalLink, ShieldCheck, Clock, Clipboard, RotateCcw, Building2,
} from "lucide-react";

/**
 * Semi-automated mailbox provisioning dialog (Option C).
 * Surfaces the user's auto-generated handle, copy-all button, deep-links to
 * Google / Microsoft / GoDaddy admin consoles, and a "Mark as provisioned"
 * toggle that updates `internal_email_provisioned` in the DB.
 */
const ProvisionMailboxDialog = ({ open, onOpenChange, userId }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/mailbox/${userId}/provision-info`);
        setInfo(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load provisioning info");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, userId]);

  const copy = (text, key) => {
    try {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey(""), 1200);
    } catch {
      toast.error("Copy blocked by browser");
    }
  };

  const copyAll = () => {
    if (!info) return;
    const p = info.proposed;
    const bundle =
      `First name: ${p.first_name}\n` +
      `Last name:  ${p.last_name}\n` +
      `Email / Username: ${p.internal_email}\n` +
      `Recovery email:   ${p.recovery_email || ""}\n` +
      `Phone:            ${p.phone || ""}`;
    copy(bundle, "all");
  };

  const togglePrivision = async (value) => {
    setSaving(true);
    try {
      const r = await api.put(`/mailbox/${userId}/mark-provisioned`, {
        provisioned: value,
        note,
        send_welcome_email: value && sendWelcome,
      });
      setInfo((prev) => prev ? { ...prev, already_provisioned: r.data.provisioned } : prev);
      if (r.data.welcome_email?.sent) {
        toast.success(`Mailbox provisioned + welcome email sent to ${r.data.welcome_email.to}`);
      } else if (value && sendWelcome && r.data.welcome_email && !r.data.welcome_email.sent) {
        toast.warning("Mailbox provisioned · welcome email skipped (SMTP not configured)");
      } else {
        toast.success(value ? "Mailbox marked as provisioned" : "Reset to pending");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    try {
      const r = await api.get(`/mailbox/${userId}/welcome-preview`);
      setPreviewHtml(r.data.html);
      setPreviewOpen(true);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const resendWelcome = async () => {
    setResending(true);
    try {
      const r = await api.post(`/mailbox/${userId}/send-welcome`);
      if (r.data.sent) {
        toast.success(`Welcome email sent to ${r.data.to}`);
      } else {
        toast.warning("SMTP not configured — configure GoDaddy SMTP in Settings to send real emails.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setResending(false);
    }
  };

  if (!userId) return null;

  const p = info?.proposed || {};
  const links = info?.admin_console_links || {};
  const provisioned = info?.already_provisioned;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="provision-mailbox-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={18} className="text-blue-600" />
            Provision Mailbox
          </DialogTitle>
          <DialogDescription>
            Create the real mailbox in your email provider's admin console and mark it as done here.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="h-48 bg-slate-100 animate-pulse rounded" />
        ) : info ? (
          <div className="space-y-4">
            {/* Status pill */}
            <div className="flex items-center gap-2">
              {provisioned ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="mailbox-status-provisioned">
                  <CheckCircle2 size={12} className="mr-1" /> Provisioned
                </Badge>
              ) : (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" data-testid="mailbox-status-pending">
                  <Clock size={12} className="mr-1" /> Pending provisioning
                </Badge>
              )}
              <span className="text-xs text-slate-500">{info.user?.full_name} · {info.user?.email}</span>
            </div>

            {/* Copyable details */}
            <div className="space-y-2" data-testid="mailbox-details">
              <FieldRow label="Email / Username" value={p.internal_email} copiedKey={copiedKey} k="internal_email" onCopy={copy} mono />
              <FieldRow label="First name" value={p.first_name} copiedKey={copiedKey} k="first_name" onCopy={copy} />
              <FieldRow label="Last name"  value={p.last_name}  copiedKey={copiedKey} k="last_name"  onCopy={copy} />
              <FieldRow label="Recovery email (fallback)" value={p.recovery_email || "—"} copiedKey={copiedKey} k="recovery_email" onCopy={copy} mono />
              <FieldRow label="Phone" value={p.phone || "—"} copiedKey={copiedKey} k="phone" onCopy={copy} />
              <Button onClick={copyAll} variant="outline" size="sm" className="w-full mt-2" data-testid="mailbox-copy-all">
                <Clipboard size={14} className="mr-2" />
                {copiedKey === "all" ? "Copied all fields ✓" : "Copy all fields as block"}
              </Button>
            </div>

            {/* Deep-links */}
            <div className="border-t pt-4 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                Open admin console (opens in new tab)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <ConsoleLink href={links.google}    label="Google Workspace" icon={Building2} testid="link-google" />
                <ConsoleLink href={links.microsoft} label="Microsoft 365"    icon={Building2} testid="link-microsoft" />
                <ConsoleLink href={links.godaddy}   label="GoDaddy Email"    icon={Building2} testid="link-godaddy" />
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                The admin console will open a blank "Add user" form. Paste the fields above to create the account,
                then return here and mark it provisioned.
              </p>
            </div>

            {/* Welcome email controls */}
            <div className="border-t pt-4 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                Welcome email
              </p>
              {!provisioned && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="welcome-email-toggle">
                  <input
                    type="checkbox"
                    checked={sendWelcome}
                    onChange={(e) => setSendWelcome(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  Send welcome email when I mark this as provisioned
                </label>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={openPreview} data-testid="welcome-email-preview">
                  Preview email
                </Button>
                {provisioned && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resendWelcome}
                    disabled={resending}
                    data-testid="welcome-email-resend"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    <Mail size={12} className="mr-1" />
                    {resending ? "Sending…" : "Resend welcome email"}
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                The email sends to the user's <strong>real registration address</strong> ({info.user?.email}). Requires SMTP configured in Settings — otherwise falls back to a dry-run log entry.
              </p>
            </div>

            {/* Note + action */}
            <div className="border-t pt-4 space-y-2">
              <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                Note (optional)
              </label>
              <Textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Temp password: …"
                className="text-sm"
                data-testid="mailbox-note"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Close</Button>
          {provisioned ? (
            <Button
              onClick={() => togglePrivision(false)}
              disabled={saving}
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="mailbox-mark-unprovisioned"
            >
              <RotateCcw size={14} className="mr-1" /> Reset to pending
            </Button>
          ) : (
            <Button
              onClick={() => togglePrivision(true)}
              disabled={saving || loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="mailbox-mark-provisioned"
            >
              <ShieldCheck size={14} className="mr-1" />
              {saving ? "Saving…" : "Mark as provisioned"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto p-0" data-testid="welcome-email-preview-dialog">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="flex items-center gap-2">
              <Mail size={16} className="text-blue-600" />
              Welcome Email Preview
            </DialogTitle>
            <DialogDescription>
              This is exactly what the user will receive.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-slate-100">
            {previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                title="Welcome email preview"
                className="w-full rounded border border-slate-200 bg-white"
                style={{ minHeight: "520px" }}
                data-testid="welcome-email-iframe"
              />
            ) : (
              <div className="h-32 bg-slate-200 animate-pulse rounded" />
            )}
          </div>
          <DialogFooter className="p-4">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close preview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

const FieldRow = ({ label, value, k, copiedKey, onCopy, mono }) => (
  <div className="flex items-center justify-between gap-2 py-1.5">
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`text-sm text-slate-900 truncate ${mono ? "font-mono" : ""}`} data-testid={`mailbox-field-${k}`}>
        {value || "—"}
      </p>
    </div>
    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" onClick={() => onCopy(value, k)} disabled={!value}
      data-testid={`mailbox-copy-${k}`}>
      {copiedKey === k ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Copy size={14} />}
    </Button>
  </div>
);

const ConsoleLink = ({ href, label, icon: Icon, testid }) => (
  <a href={href} target="_blank" rel="noreferrer noopener"
     className="flex items-center justify-center gap-2 p-3 rounded border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm font-medium text-slate-900"
     data-testid={testid}>
    <Icon size={14} className="text-blue-600" />
    {label}
    <ExternalLink size={12} className="text-slate-400" />
  </a>
);

export default ProvisionMailboxDialog;
